import { ArrowLeftRight, Coins, Eye, ListChecks, Percent, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ROUTES } from '@/app/routes';
import { buttonClasses, Logo } from '@/shared/ui';

/**
 * What a visitor who has never signed in sees at the root URL.
 *
 * ── WHY IT LIVES AT `/` AND NOT AT `/landing` ────────────────────────────────
 * It is the address people are given, paste into a chat and type from a business
 * card. A redirect to a marketing path would make every shared link show a
 * different URL than the one that was shared, and the messenger preview would
 * name the wrong page. The gate renders this instead of redirecting — see
 * `RequireAuth` — so the URL a visitor arrives at is the URL they keep.
 *
 * ── THE PAGE IS BUILT ON CONTRAST BETWEEN SECTIONS, NOT ON CARDS ─────────────
 * The first version put every section in the same rounded panel, one under
 * another, and it read exactly as it was built: six identical boxes with the
 * content crammed inside them. Nothing told the eye where one idea ended and the
 * next began, so the whole page flattened into noise.
 *
 * What separates sections now is TREATMENT, not a border:
 *
 *   hero        page background, wide type, a soft brand glow behind the art
 *   features    the only cards on the page — four of them, so they read as a set
 *   how         bare background, oversized ghost numerals carrying the rhythm
 *   advantages  bare background, hairline rules, no container at all
 *   safety      a full deep-gradient panel — the one dark beat in the sequence
 *   faq         bare background, rules only
 *   cta         gradient band
 *
 * The rule to keep: adjacent sections must not share a treatment. Wrapping the
 * next section in a panel "for consistency" is how the first version happened.
 *
 * ── EVERY CLAIM HERE IS CHECKED AGAINST THE PRODUCT ──────────────────────────
 * The numbers are the ones the backend actually charges: deposits are free on
 * both networks, a card costs 50 USDT (40 issuance + 10 landing on the card),
 * rouble payouts start at 5 000 ₽ and are priced at the partner's rate less 3 %.
 * A landing that rounds these for rhythm becomes a promise the checkout breaks,
 * and the person who finds out is a customer mid-payment. If pricing moves,
 * THIS FILE MOVES WITH IT.
 *
 * Two things are deliberately NOT claimed. There is no "bank-grade security"
 * line: customer-facing two-factor was removed from the product, and boasting
 * about protection we took out is the kind of copy that ends up quoted back at
 * us. And nothing here says a payout is instant — Walet settles when it settles.
 *
 * ── THE ILLUSTRATIONS ────────────────────────────────────────────────────────
 * SVG, generated by Recraft (`deploy/landing-graphics/`). Vector rather than 3D
 * renders for a blunt reason recorded in that script: the raster model kept
 * printing invented card numbers, and a fake card number on a payments site
 * reads as fraud. They are decorative and carry empty alt text — the heading
 * beside each one already says what the block is about, and a screen reader
 * announcing "illustration of a card" twice is noise.
 */
export default function LandingPage() {
  const { t } = useTranslation();

  const blocks = [
    { key: 'deposit', image: '/landing/deposit.svg' },
    { key: 'card', image: '/landing/card.svg' },
    { key: 'payout', image: '/landing/payout.svg' },
    { key: 'transfer', image: '/landing/transfer.svg' }
  ] as const;

  const steps = ['register', 'topUp', 'spend'] as const;

  const advantages = [
    { key: 'freeDeposit', Icon: Coins },
    { key: 'openRate', Icon: Percent },
    { key: 'oneApp', Icon: Wallet },
    { key: 'freeTransfers', Icon: ArrowLeftRight },
    { key: 'noSurprises', Icon: Eye },
    { key: 'history', Icon: ListChecks }
  ] as const;

  const safety = ['password', 'pan', 'retention', 'ledger'] as const;

  const faq = [
    'networks',
    'depositFee',
    'withdrawFee',
    'cardPrice',
    'rate',
    'timing',
    'signup'
  ] as const;

  /** Small brand-coloured label above a section heading. */
  const eyebrow = 'text-caption font-semibold uppercase tracking-[0.14em] text-[var(--brand)]';

  /** One scale for every section heading, so the page has a single voice. */
  const sectionTitle =
    'mt-3 text-title-lg font-semibold leading-tight text-[var(--text-primary)] sm:text-display';

  return (
    <div className="min-h-dvh bg-[var(--bg-muted)]">
      {/*
       * Sticky because the page is long on a phone and the one action it exists
       * for must never be more than a thumb away. Translucent with a blur rather
       * than a solid fill: content sliding under a hard edge is what makes a page
       * feel like a document rather than an app.
       */}
      <header className="sticky top-0 z-20 border-b border-[var(--border)] bg-[var(--surface)]/85 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-5 py-3 sm:px-8">
          <Logo size={32} variant="full" />

          <Link
            to={ROUTES.login}
            className={buttonClasses({ size: 'sm', shape: 'pill' })}
            data-testid="landing-signin"
          >
            {t('landing.signIn')}
          </Link>
        </div>
      </header>

      <main>
        {/* ─────────────────────────────────────────────────────────────  1. hero */}
        <section className="mx-auto grid w-full max-w-6xl items-center gap-10 px-5 pb-16 pt-14 sm:px-8 md:grid-cols-2 md:gap-16 md:pb-28 md:pt-24">
          <div className="order-2 md:order-1">
            <h1 className="text-display font-semibold leading-[1.08] text-[var(--text-primary)] md:text-[3.25rem]">
              {t('landing.hero.title')}
            </h1>

            <p className="mt-5 max-w-prose text-body text-[var(--text-secondary)] md:text-[1.0625rem]">
              {t('landing.hero.subtitle')}
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link
                to={ROUTES.register}
                className={buttonClasses({ shape: 'pill', className: 'sm:w-auto sm:px-9' })}
              >
                {t('landing.hero.primary')}
              </Link>

              <Link
                to={ROUTES.login}
                className={buttonClasses({
                  variant: 'secondary',
                  shape: 'pill',
                  className: 'sm:w-auto sm:px-9'
                })}
              >
                {t('landing.signIn')}
              </Link>
            </div>
          </div>

          {/*
           * The glow is a blurred brand disc behind the art, not a box around it.
           * It gives the illustration somewhere to sit without drawing an edge,
           * which is what made the first version feel boxed in. `-z-10` keeps it
           * strictly behind, and `pointer-events-none` keeps it out of the way.
           */}
          <div className="relative order-1 md:order-2">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 -z-10 mx-auto size-[78%] rounded-full bg-[var(--brand)]/15 blur-3xl"
            />

            <img
              src="/landing/hero.svg"
              alt=""
              width={512}
              height={512}
              /*
               * Eager AND high priority: this is the largest element above the
               * fold on a page opened from a phone, and `eager` alone only means
               * "do not defer" — it still queues behind CSS and modules.
               */
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="mx-auto w-full max-w-[20rem] md:max-w-[26rem]"
            />
          </div>
        </section>

        {/* ────────────────────────────────────────────────  2–5. what it does */}
        <section className="mx-auto w-full max-w-6xl px-5 pb-16 sm:px-8 md:pb-28">
          <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
            {blocks.map((block) => (
              <article
                key={block.key}
                className="rounded-3xl bg-[var(--surface)] p-7 shadow-[var(--shadow-sm)] sm:p-8"
              >
                <img
                  src={block.image}
                  alt=""
                  width={128}
                  height={128}
                  loading="lazy"
                  decoding="async"
                  className="size-28 sm:size-32"
                />

                <h2 className="mt-5 text-title font-semibold text-[var(--text-primary)]">
                  {t(`landing.blocks.${block.key}.title`)}
                </h2>

                <p className="mt-2 text-body-sm leading-relaxed text-[var(--text-secondary)]">
                  {t(`landing.blocks.${block.key}.body`)}
                </p>
              </article>
            ))}
          </div>
        </section>

        {/* ────────────────────────────────────────────────────  6. how to start */}
        <section className="mx-auto w-full max-w-6xl px-5 pb-16 sm:px-8 md:pb-28">
          <p className={eyebrow}>{t('landing.how.eyebrow')}</p>
          <h2 className={sectionTitle}>{t('landing.how.title')}</h2>

          {/*
           * An ordered list, not three styled divs. The numbers ARE the content:
           * a screen-reader user has to hear "1 of 3" to know this is a sequence
           * rather than three unrelated blocks, and `<ol>` says that for free.
           *
           * The numeral is oversized and half-transparent — it carries the rhythm
           * of this section instead of a container, which is what keeps it from
           * looking like the cards above it.
           */}
          <ol className="mt-10 grid gap-10 sm:grid-cols-3 sm:gap-8">
            {steps.map((step, index) => (
              <li key={step} className="relative pl-16 sm:pl-0 sm:pt-14">
                <span
                  aria-hidden
                  className="absolute left-0 top-0 text-[3.25rem] font-semibold leading-none text-[var(--brand)]/25 sm:text-[4rem]"
                >
                  {index + 1}
                </span>

                <h3 className="text-title font-semibold text-[var(--text-primary)]">
                  {t(`landing.how.steps.${step}.title`)}
                </h3>

                <p className="mt-2 text-body-sm leading-relaxed text-[var(--text-secondary)]">
                  {t(`landing.how.steps.${step}.body`)}
                </p>
              </li>
            ))}
          </ol>
        </section>

        {/* ─────────────────────────────────────────────────────  7. why bother */}
        <section className="mx-auto w-full max-w-6xl px-5 pb-16 sm:px-8 md:pb-28">
          <p className={eyebrow}>{t('landing.advantages.eyebrow')}</p>
          <h2 className={sectionTitle}>{t('landing.advantages.title')}</h2>

          <ul className="mt-10 grid gap-x-14 sm:grid-cols-2">
            {advantages.map(({ key, Icon }) => (
              <li
                key={key}
                className="flex gap-4 border-t border-[var(--border)] py-6 first:border-t-0 sm:[&:nth-child(2)]:border-t-0"
              >
                <Icon
                  aria-hidden
                  className="mt-0.5 size-5 shrink-0 text-[var(--brand)]"
                  strokeWidth={2}
                />

                <div>
                  <h3 className="text-body font-semibold text-[var(--text-primary)]">
                    {t(`landing.advantages.items.${key}.title`)}
                  </h3>

                  <p className="mt-1.5 text-body-sm leading-relaxed text-[var(--text-secondary)]">
                    {t(`landing.advantages.items.${key}.body`)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* ───────────────────────────────────────────────────────────  8. safety */}
        {/*
         * The one dark beat, and the reason it is full-bleed: after four light
         * sections the page needs somewhere to change gear, and a panel inset
         * like the cards would just be a fifth box. The DEEP gradient, never the
         * brand one — white on the brand gradient's teal end is 2.1:1. See the
         * note beside --brand-gradient-deep in tokens.css.
         */}
        <section className="brand-gradient-deep">
          <div className="mx-auto grid w-full max-w-6xl gap-10 px-5 py-16 sm:px-8 md:grid-cols-[18rem_1fr] md:items-start md:gap-16 md:py-24">
            <div>
              <img
                src="/landing/secure.svg"
                alt=""
                width={224}
                height={224}
                loading="lazy"
                decoding="async"
                className="size-40 md:size-56"
              />
            </div>

            <div>
              <h2 className="text-title-lg font-semibold leading-tight text-[var(--on-gradient)] sm:text-display">
                {t('landing.safety.title')}
              </h2>

              <p className="mt-4 max-w-prose text-body text-[var(--on-gradient)]/85">
                {t('landing.safety.intro')}
              </p>

              <ul className="mt-10 grid gap-x-12 gap-y-8 sm:grid-cols-2">
                {safety.map((item) => (
                  <li key={item}>
                    <h3 className="text-body font-semibold text-[var(--on-gradient)]">
                      {t(`landing.safety.items.${item}.title`)}
                    </h3>

                    <p className="mt-1.5 text-body-sm leading-relaxed text-[var(--on-gradient)]/85">
                      {t(`landing.safety.items.${item}.body`)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ────────────────────────────────────────────────────────────────  9. FAQ */}
        <section className="mx-auto w-full max-w-3xl px-5 py-16 sm:px-8 md:py-28">
          <p className={eyebrow}>{t('landing.faq.eyebrow')}</p>
          <h2 className={sectionTitle}>{t('landing.faq.title')}</h2>

          {/*
           * Native `<details>`, not a state-driven accordion. It opens with the
           * keyboard, it is announced as expandable without a single aria
           * attribute, it survives Ctrl+F because the browser opens a match
           * inside a closed one, and it works if the JavaScript bundle fails.
           * A hand-rolled accordion buys an animation and loses all four.
           */}
          <div className="mt-8">
            {faq.map((item) => (
              <details key={item} className="group border-b border-[var(--border)]">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-5 text-body font-medium text-[var(--text-primary)] marker:content-none [&::-webkit-details-marker]:hidden">
                  {t(`landing.faq.items.${item}.q`)}

                  <span
                    aria-hidden
                    className="shrink-0 text-title font-light leading-none text-[var(--text-muted)] transition-transform duration-200 group-open:rotate-45"
                  >
                    +
                  </span>
                </summary>

                <p className="pb-5 pe-10 text-body-sm leading-relaxed text-[var(--text-secondary)]">
                  {t(`landing.faq.items.${item}.a`)}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* ────────────────────────────────────────────────────────  closing prompt */}
        <section className="mx-auto w-full max-w-6xl px-5 pb-16 sm:px-8 md:pb-24">
          <div className="brand-gradient overflow-hidden rounded-[2rem] px-6 py-14 text-center sm:px-10">
            <h2 className="text-title-lg font-semibold leading-tight text-[var(--on-gradient)] sm:text-display">
              {t('landing.cta.title')}
            </h2>

            <p className="mx-auto mt-4 max-w-prose text-body text-[var(--on-gradient)]">
              {t('landing.cta.body')}
            </p>

            <Link
              to={ROUTES.register}
              className={buttonClasses({
                variant: 'teal',
                shape: 'pill',
                className: 'mt-8 sm:w-auto sm:px-12'
              })}
            >
              {t('landing.cta.action')}
            </Link>
          </div>
        </section>

        <footer className="mx-auto w-full max-w-6xl px-5 pb-12 text-center text-caption text-[var(--text-muted)] sm:px-8">
          {t('landing.footer')}
        </footer>
      </main>
    </div>
  );
}
